// LessonResourceCard.jsx
import React, { useCallback, useState, useEffect } from 'react';
import { createLessonResource, updateLessonResource, deleteLessonResource } from '../../../../api/LessonResourcesApis';
import '../css/LessonResourceCard.css';
import '..//css/Shared.css';

const LessonResourceCard = ({ 
  resource, 
  sectionIndex, 
  subsectionIndex, 
  lessonIndex, 
  resourceIndex, 
  sections, 
  setSections, 
  onUpdate,
  isExistingInDatabase,
  onResourceCreate,
  onResourceUpdate,
  onResourceDelete,
  lessonId
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Reset selected file when resource changes
  useEffect(() => {
    if (resource.file && resource.file instanceof File) {
      setSelectedFile(resource.file);
    } else {
      setSelectedFile(null);
    }
  }, [resource.file]);

  // Is this resource a URL type?
  const isUrlType = (resource.resource_type || 'document') === 'url';

  // Handle Create New Resource
  const handleCreateResource = async () => {
    if (isExistingInDatabase(resource)) {
      alert('This resource already exists in the database. Use Update instead.');
      return;
    }

    if (!resource.title?.trim()) {
      alert('Please enter a resource title before saving.');
      return;
    }

    if (!lessonId) {
      alert('Lesson ID is required to create a resource.');
      return;
    }

    if (isUrlType) {
      if (!resource.url?.trim()) {
        alert('Please enter a URL for this resource.');
        return;
      }
    } else {
      if (!resource.file) {
        alert('Please select a file for this resource.');
        return;
      }
    }

    setIsCreating(true);
    try {
      const resourceData = {
        title: resource.title.trim(),
        resource_type: resource.resource_type || 'document',
        order: resource.order !== undefined ? resource.order : resourceIndex,
        lesson: lessonId,
        ...(isUrlType
          ? { url: resource.url.trim() }
          : { file: resource.file }),
      };

      const response = await createLessonResource(resourceData);

      if (onResourceCreate) {
        onResourceCreate(response.data, sectionIndex, subsectionIndex, lessonIndex, resourceIndex);
      }

      alert('Resource created successfully!');
      setSelectedFile(null);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      alert(`Error creating resource: ${errorMessage}`);
    } finally {
      setIsCreating(false);
    }
  };

  // Handle Update Resource
  const handleUpdateResource = async () => {
    if (!isExistingInDatabase(resource)) {
      alert('Cannot update a resource that is not saved in the database. Please save the resource first.');
      return;
    }

    if (!resource.id) {
      alert('Resource ID is required for update.');
      return;
    }

    if (isUrlType) {
      if (!resource.url?.trim()) {
        alert('Please enter a URL for this resource.');
        return;
      }
    } else {
      if (!resource.file && !resource.file_name) {
        alert('Please select a file for this resource.');
        return;
      }
    }

    setIsUpdating(true);
    try {
      const updateData = {
        title: resource.title.trim(),
        resource_type: resource.resource_type || 'document',
        order: resource.order !== undefined ? resource.order : resourceIndex,
        lesson: resource.lesson || lessonId,
        ...(isUrlType
          ? { url: resource.url.trim() }
          : (resource.file instanceof File && { file: resource.file })),
      };

      const response = await updateLessonResource(resource.id, updateData);

      if (onResourceUpdate) {
        onResourceUpdate(resource.id, response.data);
      }

      alert('Resource updated successfully!');
      setSelectedFile(null);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      alert(`Error updating resource: ${errorMessage}`);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle Delete Resource
  const handleDeleteResource = async () => {
    if (!isExistingInDatabase(resource)) {
      deleteResourceLocal();
      return;
    }

    if (!resource.id) {
      alert('Resource ID is required for deletion.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this resource? This action cannot be undone.')) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteLessonResource(resource.id);

      if (onResourceDelete) {
        onResourceDelete(resource.id, sectionIndex, subsectionIndex, lessonIndex, resourceIndex);
      }

      alert('Resource deleted successfully!');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      alert(`Error deleting resource: ${errorMessage}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteResourceLocal = useCallback(() => {
    const updatedSections = sections.map((section, secIndex) => {
      if (secIndex === sectionIndex) {
        const updatedSubsections = section.subsections.map((sub, subIdx) => {
          if (subIdx === subsectionIndex) {
            const updatedLessons = sub.lessons.map((les, lesIdx) => {
              if (lesIdx === lessonIndex) {
                const updatedResources = les.resources.filter((_, resIdx) => resIdx !== resourceIndex);
                return { ...les, resources: updatedResources };
              }
              return les;
            });
            return { ...sub, lessons: updatedLessons };
          }
          return sub;
        });
        return { ...section, subsections: updatedSubsections };
      }
      return section;
    });
    setSections(updatedSections);
    onUpdate(updatedSections);
  }, [sectionIndex, subsectionIndex, lessonIndex, resourceIndex, sections, setSections, onUpdate]);

  const updateResource = useCallback((field, value) => {
    const updatedSections = sections.map((section, secIndex) => {
      if (secIndex === sectionIndex) {
        const updatedSubsections = section.subsections.map((sub, subIdx) => {
          if (subIdx === subsectionIndex) {
            const updatedLessons = sub.lessons.map((les, lesIdx) => {
              if (lesIdx === lessonIndex) {
                const updatedResources = les.resources.map((res, resIdx) =>
                  resIdx === resourceIndex ? { ...res, [field]: value } : res
                );
                return { ...les, resources: updatedResources };
              }
              return les;
            });
            return { ...sub, lessons: updatedLessons };
          }
          return sub;
        });
        return { ...section, subsections: updatedSubsections };
      }
      return section;
    });
    setSections(updatedSections);
    onUpdate(updatedSections);
  }, [sectionIndex, subsectionIndex, lessonIndex, resourceIndex, sections, setSections, onUpdate]);

  const handleFileUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      updateResource('file', file);
      setSelectedFile(file);

      if (!resource.title || resource.title === resource.file?.name) {
        updateResource('title', file.name);
      }
    }
  }, [resource, updateResource]);

  const handleTitleChange = useCallback((e) => {
    updateResource('title', e.target.value);
  }, [updateResource]);

  const handleResourceTypeChange = useCallback((e) => {
    const newType = e.target.value;

    // When switching type, clear the old field
    if (newType === 'url') {
      updateResource('file', null);
      setSelectedFile(null);
    } else {
      updateResource('url', '');
    }

    updateResource('resource_type', newType);
  }, [updateResource]);

  const handleUrlChange = useCallback((e) => {
    updateResource('url', e.target.value);
  }, [updateResource]);

  const getActionButton = () => {
    if (isExistingInDatabase(resource)) {
      return (
        <button
          onClick={handleUpdateResource}
          className="btn-primary btn-small"
          disabled={isUpdating}
          title="Update resource in database"
        >
          {isUpdating ? 'Updating...' : 'Update'}
        </button>
      );
    } else {
      return (
        <button
          onClick={handleCreateResource}
          className="btn-success btn-small"
          disabled={isCreating}
          title="Save new resource to database"
        >
          {isCreating ? 'Creating...' : 'Save'}
        </button>
      );
    }
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return '📄';

    const extension = fileName.split('.').pop()?.toLowerCase() || '';

    const iconMap = {
      'pdf': '📕', 'doc': '📘', 'docx': '📘', 'txt': '📄', 'rtf': '📄',
      'xls': '📊', 'xlsx': '📊', 'csv': '📊',
      'ppt': '📙', 'pptx': '📙',
      'jpg': '🖼️', 'jpeg': '🖼️', 'png': '🖼️', 'gif': '🖼️',
      'svg': '🖼️', 'bmp': '🖼️', 'webp': '🖼️',
      'zip': '📦', 'rar': '📦', '7z': '📦', 'tar': '📦', 'gz': '📦',
      'js': '💻', 'jsx': '⚛️', 'ts': '💻', 'tsx': '⚛️',
      'html': '🌐', 'css': '🎨', 'json': '📋', 'xml': '📋',
      'mp4': '🎬', 'avi': '🎬', 'mov': '🎬', 'wmv': '🎬', 'flv': '🎬',
      'mp3': '🎵', 'wav': '🎵', 'aac': '🎵', 'flac': '🎵',
    };

    return iconMap[extension] || '📄';
  };

  const getFileInfo = () => {
    if (selectedFile || (resource.file && resource.file instanceof File)) {
      const file = selectedFile || resource.file;
      const sizeInMB = file.size ? (file.size / (1024 * 1024)).toFixed(2) : 'Unknown';
      const fileType = file.type || file.name?.split('.').pop() || 'Unknown';

      return {
        name: file.name,
        size: `${sizeInMB} MB`,
        type: fileType,
        extension: file.name?.split('.').pop()?.toLowerCase() || '',
        isNew: true
      };
    }

    if (resource.file_name || resource.file) {
      const fileName = resource.file_name || resource.file?.split('.').pop() || 'Unknown file';
      let sizeInMB = 'Unknown';

      if (resource.file_size) {
        sizeInMB = (resource.file_size / (1024 * 1024)).toFixed(2);
      }

      const fileType = resource.file_type || fileName.split('.').pop() || 'Unknown';

      return {
        name: fileName,
        size: `${sizeInMB} MB`,
        type: fileType,
        extension: fileName.split('.').pop()?.toLowerCase() || '',
        isNew: false
      };
    }

    return null;
  };

  const fileInfo = getFileInfo();

  return (
    <div className={`lrc-card ${isExistingInDatabase(resource) ? 'existing' : 'new'}`}>
      <div className="lrc-header">
        <div className="lrc-order-group">
          <label className="lrc-order-label">ORDER</label>
          <input
            type="number"
            value={resource.order !== undefined ? resource.order : resourceIndex}
            onChange={(e) => updateResource('order', parseInt(e.target.value) || 0)}
            className="lrc-order-input"
            min="0"
          />
        </div>

        <input
          type="text"
          value={resource.title}
          onChange={handleTitleChange}
          placeholder="Resource title"
          className="lrc-title-input"
        />

        <select
          value={resource.resource_type || 'document'}
          onChange={handleResourceTypeChange}
          className="lrc-type-select"
        >
          <option value="document">Document</option>
          <option value="image">Image</option>
          <option value="archive">Archive</option>
          <option value="video">Video</option>
          <option value="audio">Audio</option>
          <option value="url">URL</option>
          <option value="other">Other</option>
        </select>

        <div className="lrc-status">
          <span className={`lrc-status-badge ${isExistingInDatabase(resource) ? 'existing' : 'new'}`}>
            {isExistingInDatabase(resource) ? 'EXISTING' : 'NEW'}
          </span>
          {isCreating && <span className="creating-badge">CREATING...</span>}
          {isUpdating && <span className="updating-badge">UPDATING...</span>}
        </div>

        <div className="lrc-actions">
          {getActionButton()}
          <button
            onClick={handleDeleteResource}
            className="btn-danger btn-small"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>

      {/* Source section — driven by resource_type */}
      {isUrlType ? (
        <div className="lrc-file-section">
          <div className="lrc-url-input-group">
            <label
              className="lrc-url-input-label"
              htmlFor={`resource-url-${sectionIndex}-${subsectionIndex}-${lessonIndex}-${resourceIndex}`}
            >
              🔗 URL
            </label>
            <input
              type="url"
              id={`resource-url-${sectionIndex}-${subsectionIndex}-${lessonIndex}-${resourceIndex}`}
              value={resource.url || ''}
              onChange={handleUrlChange}
              placeholder="https://example.com/resource.pdf"
              className="lrc-url-input"
            />
            <small className="lrc-url-input-hint">
              Paste a link to a document, video, article or any external resource.
            </small>
          </div>
        </div>
      ) : (
        <div className="lrc-file-section">
          {!fileInfo ? (
            <div className="lrc-upload-area">
              <label
                htmlFor={`resource-file-${sectionIndex}-${subsectionIndex}-${lessonIndex}-${resourceIndex}`}
                className="lrc-file-label"
              >
                📁 Choose File
              </label>
              <span className="lrc-upload-hint">
                Supports: PDF, Word, Excel, PPT, Images, ZIP, Video, Audio and more
              </span>
            </div>
          ) : (
            <div className="lrc-file-info">
              <div className="lrc-file-name">
                <span className="file-icon">{getFileIcon(fileInfo.name)}</span>
                <strong>File:</strong> {fileInfo.name}
                {fileInfo.isNew && <span className="file-new-badge">NEW</span>}
              </div>
              <div className="lrc-file-details">
                <span className="file-size">📊 Size: {fileInfo.size}</span>
                <span className="file-type">📂 Type: {fileInfo.type}</span>
                {fileInfo.extension && (
                  <span className="file-extension">🏷️ .{fileInfo.extension}</span>
                )}
              </div>
              <div className="lrc-file-actions">
                <button
                  type="button"
                  onClick={() => {
                    document
                      .getElementById(
                        `resource-file-${sectionIndex}-${subsectionIndex}-${lessonIndex}-${resourceIndex}`
                      )
                      ?.click();
                  }}
                  className="btn-secondary btn-small"
                >
                  {/* Label reflects what's actually being replaced:
                      - Existing file in DB      → "Replace File"
                      - New file not yet saved   → "Change File"
                  */}
                  {isExistingInDatabase(resource) ? '📤 Replace File' : '🔄 Change File'}
                </button>
              </div>
            </div>
          )}

          <input
            type="file"
            id={`resource-file-${sectionIndex}-${subsectionIndex}-${lessonIndex}-${resourceIndex}`}
            onChange={handleFileUpload}
            className="lrc-file-input"
            style={{ display: 'none' }}
          />
        </div>
      )}

      {isExistingInDatabase(resource) && (resource.file || resource.file_name || resource.url) && (
        <div className="lrc-database-indicator">
          <span className="database-icon">🗃️</span>
          <small>{isUrlType ? 'Link stored in database' : 'File stored in database'}</small>
        </div>
      )}
    </div>
  );
};

LessonResourceCard.defaultProps = {
  onResourceCreate: () => {},
  onResourceUpdate: () => {},
  onResourceDelete: () => {}
};

export default LessonResourceCard;